<template>
  <v-card elevation="2">
    <v-card-title class="d-flex align-center">
      <v-icon left>mdi-clipboard-list-outline</v-icon>
      <h3>Purchase Requests</h3>
      <v-spacer></v-spacer>
      <v-btn icon @click="refreshTable" :loading="loading" color="primary">
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
    </v-card-title>

    <v-card-text>
      <v-row>
        <v-col cols="12" md="3">
          <v-text-field
            v-model="search"
            label="Search"
            prepend-icon="mdi-magnify"
            clearable
            outlined
            dense
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="3">
          <v-select
            v-model="filterBranch"
            :items="branchFilterOptions"
            label="Filter by Branch"
            prepend-icon="mdi-office-building"
            outlined
            dense
            @change="fetchOrders"
          ></v-select>
        </v-col>
        <v-col cols="12" md="3">
          <v-select
            v-model="filterPurpose"
            :items="purposeFilterOptions"
            label="Filter by Purpose"
            prepend-icon="mdi-filter"
            outlined
            dense
          ></v-select>
        </v-col>
        <v-col cols="12" md="3">
          <v-select
            v-model="filterStatus"
            :items="['All', ...statusOptions]"
            label="Filter by Status"
            prepend-icon="mdi-flag"
            outlined
            dense
          ></v-select>
        </v-col>
      </v-row>

      <v-data-table
        :headers="headers"
        :items="filteredOrders"
        :search="search"
        :loading="loading"
        class="elevation-1"
        :items-per-page="10"
        :footer-props="{
          'items-per-page-options': [5, 10, 25, 50, -1],
        }"
      >
        <!-- PO Date -->
        <template v-slot:[`item.po_date`]="{ item }">
          {{ formatDate(item.po_date) }}
        </template>

        <!-- Purpose -->
        <template v-slot:[`item.purpose`]="{ item }">
          <v-chip :color="getPurposeColor(item.purpose)" dark small>
            {{ getPurposeLabel(item.purpose) }}
          </v-chip>
        </template>

        <!-- Description -->
        <template v-slot:[`item.description`]="{ item }">
          <span v-if="item.description">{{ item.description }}</span>
          <span v-else class="grey--text">--</span>
        </template>

        <!-- Status -->
        <template v-slot:[`item.status`]="{ item }">
          <v-chip :color="getStatusColor(item.status)" dark small>
            {{ item.status }}
          </v-chip>
        </template>

        <!-- Created By -->
        <template v-slot:[`item.created_by`]="{ item }">
          <span v-if="item.created_by">{{ item.created_by.name }}</span>
          <span v-else class="grey--text">--</span>
        </template>

        <!-- Attachments -->
        <template v-slot:[`item.attachments`]="{ item }">
          <v-menu
            v-if="item.attachments && item.attachments.length > 0"
            offset-y
          >
            <template v-slot:activator="{ on, attrs }">
              <v-badge
                :content="item.attachments.length"
                color="primary"
                overlap
              >
                <v-btn
                  icon
                  color="primary"
                  v-bind="attrs"
                  v-on="on"
                  title="View Attachments"
                >
                  <v-icon>mdi-paperclip</v-icon>
                </v-btn>
              </v-badge>
            </template>
            <v-list dense>
              <v-list-item
                v-for="att in item.attachments"
                :key="att.id"
                @click="viewAttachment(att.file_path)"
              >
                <v-list-item-icon class="mr-2">
                  <v-icon small>mdi-file</v-icon>
                </v-list-item-icon>
                <v-list-item-title>
                  {{ att.original_name || "Attachment" }}
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <span v-else class="grey--text">--</span>
        </template>

        <!-- Actions -->
        <template v-slot:[`item.actions`]="{ item }">
          <div class="d-flex">
            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  color="primary"
                  v-bind="attrs"
                  v-on="on"
                  title="Print"
                >
                  <v-icon>mdi-printer</v-icon>
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item @click="printItem(item)">
                  <v-list-item-icon class="mr-2">
                    <v-icon small>mdi-file-pdf-box</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Print / Save as PDF</v-list-item-title>
                </v-list-item>
                <v-list-item @click="copyAsImage(item)">
                  <v-list-item-icon class="mr-2">
                    <v-icon small>mdi-content-copy</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Copy as Image</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
            <v-btn icon color="warning" @click="editItem(item)" title="Edit">
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
            <v-btn
              icon
              color="error"
              @click="deleteItem(item)"
              title="Delete"
              :loading="item.deleting"
            >
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </div>
        </template>

        <!-- No Data -->
        <template v-slot:no-data>
          <div class="text-center py-6">
            <v-icon size="64" color="grey lighten-1"
              >mdi-clipboard-list-outline</v-icon
            >
            <p class="grey--text mt-2">No internal purchase orders found</p>
          </div>
        </template>
      </v-data-table>
    </v-card-text>

    <!-- Add/Edit Dialog -->
    <v-dialog v-model="dialog" max-width="900px" persistent scrollable>
      <v-card>
        <v-card-title class="primary white--text">
          <v-icon left color="white">{{
            dialogMode === "add" ? "mdi-plus" : "mdi-pencil"
          }}</v-icon>
          {{ dialogMode === "add" ? "Add" : "Edit" }} Purchase Request
          <v-spacer></v-spacer>
          <v-btn icon dark @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-6">
          <v-form ref="form" v-model="formValid">
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editedItem.po_date"
                  label="PO Date"
                  type="date"
                  prepend-icon="mdi-calendar"
                  :rules="[rules.required]"
                  outlined
                  dense
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-select
                  v-model="editedItem.purpose"
                  :items="purposeOptions"
                  label="Purpose"
                  prepend-icon="mdi-tag"
                  :rules="[rules.required]"
                  outlined
                  dense
                ></v-select>
              </v-col>

              <v-col cols="12">
                <v-textarea
                  v-model="editedItem.description"
                  label="Description"
                  prepend-icon="mdi-text"
                  outlined
                  dense
                  rows="2"
                ></v-textarea>
              </v-col>

              <!-- Items Section -->
              <v-col cols="12">
                <v-card outlined class="mb-2">
                  <v-card-title class="py-2 px-4">
                    <v-icon left small>mdi-format-list-bulleted</v-icon>
                    <span class="subtitle-1 font-weight-bold">Items</span>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" small @click="addItemRow">
                      <v-icon left small>mdi-plus</v-icon>
                      Add Item
                    </v-btn>
                  </v-card-title>

                  <v-divider></v-divider>

                  <v-simple-table v-if="editedItem.items.length > 0" dense>
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th style="width: 40px">#</th>
                          <th>Item Type / Description *</th>
                          <th style="width: 90px">Qty *</th>
                          <th style="width: 110px">Unit</th>
                          <th>Remarks</th>
                          <th style="width: 50px"></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(item, index) in editedItem.items"
                          :key="index"
                        >
                          <td class="text-center">{{ index + 1 }}</td>
                          <td>
                            <v-text-field
                              v-model="item.item_name"
                              placeholder="Item description"
                              dense
                              hide-details
                              class="mt-1 mb-1"
                            ></v-text-field>
                          </td>
                          <td>
                            <v-text-field
                              v-model.number="item.qty"
                              type="number"
                              min="1"
                              placeholder="1"
                              dense
                              hide-details
                              class="mt-1 mb-1"
                            ></v-text-field>
                          </td>
                          <td>
                            <v-text-field
                              v-model="item.unit"
                              placeholder="pcs, set, etc."
                              dense
                              hide-details
                              class="mt-1 mb-1"
                            ></v-text-field>
                          </td>
                          <td>
                            <v-text-field
                              v-model="item.remarks"
                              placeholder="Optional remarks"
                              dense
                              hide-details
                              class="mt-1 mb-1"
                            ></v-text-field>
                          </td>
                          <td>
                            <v-btn
                              icon
                              color="error"
                              @click="removeItemRow(index)"
                              :disabled="editedItem.items.length <= 1"
                            >
                              <v-icon>mdi-close</v-icon>
                            </v-btn>
                          </td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>

                  <div v-else class="text-center py-4 grey--text">
                    No items added. Click "Add Item" to add rows.
                  </div>
                </v-card>
              </v-col>

              <v-col cols="12">
                <v-select
                  v-model="editedItem.status"
                  :items="statusOptions"
                  label="Status"
                  prepend-icon="mdi-flag"
                  :rules="[rules.required]"
                  outlined
                  dense
                ></v-select>
              </v-col>

              <!-- Existing Attachments -->
              <v-col
                v-if="
                  editedItem.attachments && editedItem.attachments.length > 0
                "
                cols="12"
              >
                <p class="subtitle-2 mb-2">
                  <v-icon small class="mr-1">mdi-paperclip</v-icon>
                  Attachments ({{ editedItem.attachments.length }})
                </p>
                <v-chip
                  v-for="att in editedItem.attachments"
                  :key="att.id"
                  class="mr-2 mb-1"
                  close
                  @click:close="removeExistingAttachment(att)"
                  @click="viewAttachment(att.file_path)"
                  outlined
                  color="primary"
                >
                  <v-icon left small>mdi-file</v-icon>
                  {{ att.original_name || "Attachment" }}
                </v-chip>
              </v-col>

              <!-- Add Attachments -->
              <v-col cols="12">
                <v-file-input
                  v-model="attachmentFiles"
                  label="Attachments (JPEG, PNG, GIF, PDF)"
                  prepend-icon="mdi-paperclip"
                  accept=".jpg,.jpeg,.png,.gif,.pdf"
                  outlined
                  dense
                  clearable
                  show-size
                  multiple
                  :hint="
                    attachmentFiles && attachmentFiles.length > 0
                      ? attachmentFiles.length + ' file(s) selected'
                      : ''
                  "
                  persistent-hint
                ></v-file-input>
                <div
                  v-if="attachmentFiles && attachmentFiles.length > 0"
                  class="mb-2"
                >
                  <v-chip
                    v-for="(file, idx) in attachmentFiles"
                    :key="idx"
                    close
                    small
                    class="mr-2 mb-1"
                    @click:close="attachmentFiles.splice(idx, 1)"
                  >
                    {{ file.name }}
                  </v-chip>
                </div>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-card-actions class="px-6 pb-4">
          <v-btn
            color="primary"
            :disabled="!formValid"
            :loading="submitting"
            @click="saveItem"
          >
            <v-icon left>mdi-content-save</v-icon>
            {{ dialogMode === "add" ? "Save" : "Update" }}
          </v-btn>
          <v-btn text @click="closeDialog">
            <v-icon left>mdi-close</v-icon>
            Cancel
          </v-btn>
          <v-spacer></v-spacer>
          <p v-if="editedItem.id" class="caption grey--text mb-0">
            ID: {{ editedItem.id }}
          </p>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-divider></v-divider>
    <v-card-actions>
      <v-spacer></v-spacer>
      <p class="caption grey--text mb-0">internalPurchaseOrderTable.vue</p>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import {
  printInternalPurchaseOrder,
  copyInternalPurchaseOrderAsImage,
} from "@/utils/internalPurchaseOrderPrint";

export default {
  name: "InternalPurchaseOrderTable",

  data() {
    return {
      search: "",
      filterBranch: null,
      filterPurpose: "All",
      filterStatus: "All",
      dialog: false,
      dialogMode: "add",
      formValid: false,
      submitting: false,

      attachmentFiles: [],
      uploadProgress: 0,
      copyingImage: false,

      editedItem: {
        id: null,
        po_date: "",
        purpose: null,
        description: "",
        status: "Pending",
        attachments: [],
        items: [{ item_name: "", qty: 1, unit: "", remarks: "" }],
      },

      statusOptions: ["Pending", "Finished"],

      purposeOptions: [
        { text: "Tools & Equipment", value: "tools_and_equipment" },
        { text: "Restock", value: "restock" },
        {
          text: "Item Order for Government PO",
          value: "customer_po_fulfillment",
        },
        { text: "Others", value: "others" },
      ],

      headers: [
        { text: "Actions", value: "actions", sortable: false, width: 150 },
        { text: "ID", value: "id", width: 60 },
        { text: "PO Date", value: "po_date", width: 120 },
        { text: "Purpose", value: "purpose", width: 190 },
        { text: "Description", value: "description" },
        { text: "Status", value: "status", width: 150 },
        { text: "Files", value: "attachments", sortable: false, width: 70 },
        {
          text: "Created By",
          value: "created_by",
          sortable: false,
          width: 130,
        },
      ],

      rules: {
        required: (value) => !!value || "This field is required",
      },
    };
  },

  computed: {
    ...mapGetters({
      internal_purchase_orders:
        "internal_purchase_order/internal_purchase_orders",
      loading: "internal_purchase_order/loading",
      default_server_url: "auth/default_server_url",
      user: "auth/user",
      branches: "branch/branches",
    }),

    purposeFilterOptions() {
      return [{ text: "All", value: "All" }, ...this.purposeOptions];
    },

    branchFilterOptions() {
      return [
        { text: "All Branches", value: "all" },
        ...this.branches.map((b) => ({ text: b.branch_name, value: b.id })),
      ];
    },

    filteredOrders() {
      let filtered = this.internal_purchase_orders;

      if (this.filterPurpose && this.filterPurpose !== "All") {
        filtered = filtered.filter((po) => po.purpose === this.filterPurpose);
      }

      if (this.filterStatus && this.filterStatus !== "All") {
        filtered = filtered.filter((po) => po.status === this.filterStatus);
      }

      return filtered;
    },
  },

  methods: {
    ...mapActions({
      get_internal_purchase_orders:
        "internal_purchase_order/get_internal_purchase_orders",
      add_internal_purchase_order:
        "internal_purchase_order/add_internal_purchase_order",
      update_internal_purchase_order:
        "internal_purchase_order/update_internal_purchase_order",
      delete_internal_purchase_order:
        "internal_purchase_order/delete_internal_purchase_order",
      delete_attachment: "internal_purchase_order/delete_attachment",
      get_branches: "branch/get_branches",
    }),

    async fetchOrders() {
      await this.get_internal_purchase_orders(this.filterBranch);
    },

    async refreshTable() {
      await this.fetchOrders();
      this.$emit("notification", "Table refreshed", "info");
    },

    emptyItem() {
      return {
        id: null,
        po_date: new Date().toISOString().substr(0, 10),
        purpose: null,
        description: "",
        status: "Pending",
        items: [{ item_name: "", qty: 1, unit: "", remarks: "" }],
      };
    },

    addItemRow() {
      this.editedItem.items.push({
        item_name: "",
        qty: 1,
        unit: "",
        remarks: "",
      });
    },

    removeItemRow(index) {
      if (this.editedItem.items.length > 1) {
        this.editedItem.items.splice(index, 1);
      }
    },

    printItem(item) {
      printInternalPurchaseOrder(item);
    },

    async copyAsImage(item) {
      if (this.copyingImage) return;
      this.copyingImage = true;
      try {
        await copyInternalPurchaseOrderAsImage(item);
        this.$emit(
          "notification",
          "Internal purchase order copied to clipboard as image!",
          "success"
        );
      } catch (error) {
        console.error("Error copying as image:", error);
        this.$emit(
          "notification",
          "Failed to copy as image. Please try again.",
          "error"
        );
      } finally {
        this.copyingImage = false;
      }
    },

    openAddDialog() {
      this.editedItem = this.emptyItem();
      this.attachmentFiles = [];
      this.dialogMode = "add";
      this.dialog = true;
    },

    editItem(item) {
      this.editedItem = JSON.parse(
        JSON.stringify({
          ...this.emptyItem(),
          ...item,
          description: item.description || "",
          attachments: item.attachments || [],
          items:
            item.items && item.items.length > 0
              ? item.items
              : [{ item_name: "", qty: 1, unit: "", remarks: "" }],
        })
      );
      this.attachmentFiles = [];
      this.dialogMode = "edit";
      this.dialog = true;
    },

    async saveItem() {
      if (!this.$refs.form.validate()) {
        return;
      }

      this.submitting = true;
      this.uploadProgress = 0;
      try {
        const payload = {
          po_date: this.editedItem.po_date,
          purpose: this.editedItem.purpose,
          description: this.editedItem.description || null,
          status: this.editedItem.status,
          items: this.editedItem.items,
        };

        const onUploadProgress = (e) => {
          if (e.total) {
            this.uploadProgress = Math.round((e.loaded * 100) / e.total);
          }
        };

        if (this.attachmentFiles && this.attachmentFiles.length > 0) {
          // Use FormData for file upload
          const formData = new FormData();
          Object.keys(payload).forEach((key) => {
            if (key === "items") {
              formData.append(key, JSON.stringify(payload[key]));
            } else if (payload[key] !== null && payload[key] !== undefined) {
              formData.append(key, payload[key]);
            }
          });
          this.attachmentFiles.forEach((file) => {
            formData.append("attachments[]", file);
          });

          if (this.dialogMode === "add") {
            await this.add_internal_purchase_order({
              _formData: formData,
              onUploadProgress,
            });
            this.$emit(
              "notification",
              "Internal purchase order added successfully!",
              "success"
            );
          } else {
            formData.append("id", this.editedItem.id);
            formData.append("_method", "PUT");
            await this.update_internal_purchase_order({
              _formData: formData,
              onUploadProgress,
            });
            this.$emit(
              "notification",
              "Internal purchase order updated successfully!",
              "success"
            );
          }
        } else if (this.dialogMode === "add") {
          await this.add_internal_purchase_order(payload);
          this.$emit(
            "notification",
            "Internal purchase order added successfully!",
            "success"
          );
        } else {
          await this.update_internal_purchase_order({
            id: this.editedItem.id,
            ...payload,
          });
          this.$emit(
            "notification",
            "Internal purchase order updated successfully!",
            "success"
          );
        }
        this.closeDialog();
      } catch (error) {
        console.error("Error saving internal purchase order:", error);
        this.$emit(
          "notification",
          this.errorMessage(error, "Failed to save internal purchase order"),
          "error"
        );
      } finally {
        this.submitting = false;
      }
    },

    async deleteItem(item) {
      const confirmed = await this.$confirm({
        title: "Delete Purchase Request",
        message: `Are you sure you want to delete purchase request #${item.id}?`,
        color: "red",
        icon: "mdi-delete",
      });

      if (!confirmed) return;

      this.$set(item, "deleting", true);

      try {
        await this.delete_internal_purchase_order(item.id);
        this.$emit(
          "notification",
          "Internal purchase order deleted successfully!",
          "success"
        );
      } catch (error) {
        console.error("Error deleting internal purchase order:", error);
        this.$emit(
          "notification",
          this.errorMessage(error, "Failed to delete internal purchase order"),
          "error"
        );
      } finally {
        this.$set(item, "deleting", false);
      }
    },

    closeDialog() {
      this.dialog = false;
      setTimeout(() => {
        this.editedItem = this.emptyItem();
        this.attachmentFiles = [];
        this.dialogMode = "add";
        if (this.$refs.form) {
          this.$refs.form.resetValidation();
        }
      }, 300);
    },

    viewAttachment(filePath) {
      if (filePath) {
        const path = filePath.startsWith("uploads/")
          ? filePath
          : `storage/${filePath}`;
        const url = `${this.default_server_url}/${path}`;
        window.open(url, "_blank");
      }
    },

    async removeExistingAttachment(att) {
      try {
        await this.delete_attachment({
          poId: this.editedItem.id,
          attachmentId: att.id,
        });
        this.editedItem.attachments = this.editedItem.attachments.filter(
          (a) => a.id !== att.id
        );
        this.$emit("notification", "Attachment removed", "success");
      } catch (error) {
        console.error("Error deleting attachment:", error);
        this.$emit(
          "notification",
          this.errorMessage(error, "Failed to delete attachment"),
          "error"
        );
      }
    },

    errorMessage(error, fallback) {
      const data = error.response && error.response.data;
      if (data && data.errors) {
        const first = Object.values(data.errors)[0];
        if (first && first.length) return first[0];
      }
      return (data && data.message) || fallback;
    },

    formatDate(date) {
      if (!date) return "N/A";
      return new Date(date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },

    getStatusColor(status) {
      const colors = {
        Pending: "orange",
        Finished: "green",
      };
      return colors[status] || "grey";
    },

    getPurposeColor(purpose) {
      const colors = {
        tools_and_equipment: "purple",
        restock: "teal",
        customer_po_fulfillment: "blue",
        others: "brown",
      };
      return colors[purpose] || "grey";
    },

    getPurposeLabel(purpose) {
      const found = this.purposeOptions.find((p) => p.value === purpose);
      return found ? found.text : purpose;
    },
  },

  mounted() {
    this.filterBranch =
      this.user && this.user.branch_id ? this.user.branch_id : "all";
    if (this.branches.length === 0) {
      this.get_branches();
    }
    this.fetchOrders();
  },
};
</script>

<style scoped>
.v-card {
  border-radius: 8px;
}
</style>
